import { describeCollectedField } from '@lume/shared/collected-data';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  deriveSlot,
  formatDateKeyBR,
  personBondLine,
  statusMeta,
  typeLabel,
} from './agenda-adapter';

import type { AppointmentWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

export type AppointmentAction = 'reschedule' | 'no-show' | 'complete' | 'cancel';

function Section({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-4">
      <div
        className="mb-1 text-xs font-medium uppercase"
        style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ color: 'var(--fg-1)' }}>
        {children}
      </div>
    </div>
  );
}

const ACTIONABLE = new Set(['scheduled', 'confirmed', 'imminent']);

/**
 * AppointmentDrawer — detalhe (read) + ações. Apenas campos REAIS do
 * `SerializedAppointment` — sem chain-history/audit/confirmação fabricados (§4).
 */
export function AppointmentDrawer({
  appt,
  onClose,
  onAction,
}: {
  appt: AppointmentWireItem;
  onClose: () => void;
  onAction: (action: AppointmentAction, appt: AppointmentWireItem) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const convId = appt.conversationId;
  const slot = deriveSlot(appt);
  const meta = statusMeta(appt.status);
  const canAct = ACTIONABLE.has(appt.status);
  const identity = personBondLine(appt); // "Pessoa · Bond" — vazio quando avulso (§4)

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label={appt.title}
        className="flex h-full w-full max-w-md flex-col shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <header
          className="flex items-start gap-2 p-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <button
            type="button"
            className="btn btn--ghost btn--sm btn--icon"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={14} />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--fg-1)' }}>
              {typeLabel(appt.type)} · {appt.title}
            </h2>
            {identity !== '' && (
              <div className="truncate text-xs" style={{ color: 'var(--fg-2)' }}>
                {identity}
              </div>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4">
          <Section label="Data e duração">
            {formatDateKeyBR(slot.dateKey)} · {slot.start}–{slot.end} ({appt.durationMinutes} min)
          </Section>

          <Section label="Status">
            <span
              className={`appt-card appt-card--${meta.tone}`}
              style={{ display: 'inline-block', padding: '2px 8px' }}
            >
              {meta.label}
            </span>
            {appt.rescheduledFromId !== null && (
              <span
                className="mono"
                style={{ marginLeft: 8, fontSize: 11, color: 'var(--info-fg)' }}
              >
                Reagendado
              </span>
            )}
          </Section>

          {appt.addressOrLocation !== null && appt.addressOrLocation.trim().length > 0 && (
            <Section label="Endereço / local">{appt.addressOrLocation}</Section>
          )}

          {appt.notes !== null && appt.notes.trim().length > 0 && (
            <Section label="Notas">{appt.notes}</Section>
          )}

          {/* ADR-155 Fase 4 (E1/§3.3) — envelope episódico, SEMPRE read-only
              (a escrita é exclusiva do dispatcher da Coleta na conversa em
              foco). Só aparece com dado coletado — nada a mostrar antes de
              qualquer coleta. */}
          {appt.collectedData !== null && Object.keys(appt.collectedData).length > 0 && (
            <Section label="Dados desta consulta">
              <div className="flex flex-col gap-2">
                {Object.entries(appt.collectedData).map(([name, entry]) => {
                  const described = describeCollectedField(name, entry);
                  return (
                    <div key={name} data-testid={`appt-collected-field-${name}`}>
                      <div className="text-sm" style={{ color: 'var(--fg-1)' }}>
                        {described.label}: {described.value}
                      </div>
                      <div className="text-2xs" style={{ color: 'var(--fg-3)' }}>
                        {described.meta}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {convId !== null && (
            <Section label="Conversa relacionada">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => navigate(`/atendimento/conversation/${convId}`)}
              >
                Abrir conversa
              </button>
            </Section>
          )}

          {appt.status === 'no_show' && appt.noShowGeneratedPendencyId !== null && (
            <Section label="Follow-up gerado">
              Pendência de contato criada — disponível na sua caixa de Pendências.
            </Section>
          )}

          {appt.cancellationReason !== null && appt.cancellationReason.trim().length > 0 && (
            <Section label="Motivo do cancelamento">{appt.cancellationReason}</Section>
          )}
        </div>

        {canAct && (
          <footer
            className="flex items-center gap-2 p-4"
            style={{ borderTop: '1px solid var(--border-default)' }}
          >
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => onAction('reschedule', appt)}
            >
              Reagendar
            </button>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => onAction('no-show', appt)}
            >
              Não compareceu
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => onAction('complete', appt)}
            >
              Concluir
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              style={{ marginLeft: 'auto', color: 'var(--danger-fg)' }}
              onClick={() => onAction('cancel', appt)}
            >
              Cancelar
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
